import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, refreshProfile } = useAuth();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [state, setState] = useState("checking"); // checking | ok | already | error
  const [err, setErr] = useState("");
  // Ref keeps the latest auth state without re-triggering the one-shot
  // verification effect (the token is single-use).
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!token) { setErr("Verification token is missing."); setState("error"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState(data?.already ? "already" : "ok");
        if (userRef.current) refreshProfile();
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
        setState("error");
      }
    })();
  }, [token, refreshProfile]);

  return (
    <div className="min-h-screen bg-newsprint flex items-center justify-center px-6" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-white border border-gray-300 rounded-2xl p-8 fade-up shadow-[6px_6px_0_0_rgba(124,58,237,0.08)]">
        {state === "checking" && (
          <>
            <Loader2 size={26} className="animate-spin text-[#7C3AED]" />
            <h1 className="font-display text-3xl tracking-[-0.02em] font-medium mt-5">Verifying…</h1>
          </>
        )}

        {(state === "ok" || state === "already") && (
          <div data-testid="verify-ok">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-gradient text-white">
              <Check size={22} strokeWidth={2} />
            </div>
            <h1 className="font-display text-3xl tracking-[-0.02em] font-medium mt-5">
              {state === "already" ? "Already verified." : "You're verified."}
            </h1>
            <p className="text-sm text-gray-700 mt-3 leading-relaxed">
              {state === "already"
                ? "Your email was already confirmed. Carry on."
                : "Your email is confirmed. Daily Brief delivery and account recovery are now enabled."}
            </p>
            <button
              onClick={() => nav(user ? "/app" : "/login")}
              className="mt-6 bg-black text-white hover:bg-gray-800 rounded-lg px-6 py-3 font-medium text-sm"
            >
              {user ? "Open Fount" : "Sign in"}
            </button>
          </div>
        )}

        {state === "error" && (
          <div data-testid="verify-error">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-red-600 text-white">
              <X size={22} strokeWidth={2} />
            </div>
            <h1 className="font-display text-3xl tracking-[-0.02em] font-medium mt-5">
              Couldn&apos;t verify.
            </h1>
            <p className="text-sm text-gray-700 mt-3 leading-relaxed">{err}</p>
            <div className="mt-6 flex items-center gap-3">
              <Link to="/login" className="bg-black text-white hover:bg-gray-800 rounded-lg px-5 py-2.5 text-sm font-medium">
                Sign in
              </Link>
              <Link to="/" className="border border-gray-300 hover:border-black rounded-lg px-5 py-2.5 text-sm font-medium">
                Home
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
